import {
  Trophy,
  Award,
  Star,
  CalendarHeart,
  CalendarClock,
  PartyPopper,
  Megaphone,
  Sparkles,
  AlertTriangle,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  useMyRecords,
  useMe,
  useMyBalance,
  useLeaveRequests,
  useHolidays,
  useAnnouncements,
  type EmployeeRecord,
  type AnnouncementListItem,
} from '@/lib/queries';
import { useAuthStore } from '@/store/auth.store';
import { initials, formatDateOnly } from '@isoft-people/utils';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PageTransition, FadeIn } from '@/components/ui/motion';
import { AnnouncementMedia } from '@/components/AnnouncementMedia';

function fmtDate(d: string) {
  return formatDateOnly(d, { day: 'numeric', month: 'long', year: 'numeric' });
}
function fmtShort(d: string) {
  return formatDateOnly(d, { day: 'numeric', month: 'short' });
}
// Día/mes de una fecha date-only sin corrimiento de zona.
function dateParts(d: string) {
  const [y, m, day] = d.slice(0, 10).split('-').map(Number);
  const local = new Date(y, (m || 1) - 1, day || 1);
  return {
    day: local.getDate(),
    month: local.toLocaleDateString('es', { month: 'short' }),
  };
}

export function CollaboratorHome() {
  const user = useAuthStore((s) => s.user);
  const firstName = user?.fullName?.split(' ')[0] ?? 'Colaborador';
  const employeeId = user?.employeeId;

  const { data: records } = useMyRecords();
  const { data: me } = useMe();
  const { data: balance } = useMyBalance();
  const { data: myLeaves } = useLeaveRequests({ employeeId, status: 'APPROVED' });
  const countryId = me?.employee?.country?.id;
  const year = new Date().getFullYear();
  const { data: holidays } = useHolidays(countryId, year);
  const { data: announcements } = useAnnouncements({ status: 'PUBLISHED', pageSize: 10 });

  const awards = records?.awards ?? [];
  const warnings = records?.warnings ?? [];
  const totalPoints = records?.totalPoints ?? 0;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Próximas vacaciones aprobadas (futuras)
  const upcomingLeaves = (myLeaves?.items ?? [])
    .filter((l: any) => new Date(l.startDate) >= today)
    .sort((a: any, b: any) => +new Date(a.startDate) - +new Date(b.startDate))
    .slice(0, 4);

  // Próximos feriados del país
  const upcomingHolidays = (holidays?.items ?? [])
    .filter((h: any) => new Date(h.date) >= today)
    .sort((a: any, b: any) => +new Date(a.date) - +new Date(b.date))
    .slice(0, 4);

  const daysAvailable =
    balance?.available ?? balance?.daysAvailable ?? balance?.remaining ?? null;

  const posts = announcements?.items ?? [];

  return (
    <PageTransition className="flex flex-col gap-6">
      {/* Hero con premios */}
      <FadeIn>
        <div className="brand-gradient relative overflow-hidden rounded-2xl px-7 py-7 text-white">
          <div className="relative z-10 flex flex-wrap items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/80">
                <Sparkles size={14} /> Tu espacio
              </div>
              <h1 className="mt-2 text-2xl font-bold">Hola, {firstName}</h1>
              <p className="mt-1 text-sm text-white/85">
                Estos son tus reconocimientos y lo que viene para ti.
              </p>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-center">
                <div className="flex items-center justify-center gap-1.5 text-3xl font-bold">
                  <Trophy size={24} /> {awards.length}
                </div>
                <p className="text-xs text-white/80">Premios</p>
              </div>
              {totalPoints > 0 && (
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1.5 text-3xl font-bold">
                    <Star size={22} /> {totalPoints}
                  </div>
                  <p className="text-xs text-white/80">Puntos</p>
                </div>
              )}
            </div>
          </div>
          <div className="pointer-events-none absolute -right-8 -top-10 h-44 w-44 rounded-full bg-white/10 anim-float" />
        </div>
      </FadeIn>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Columna principal: Premios + Anuncios */}
        <div className="flex flex-col gap-6 lg:col-span-2">
          {/* Premios / reconocimientos */}
          <FadeIn delay={0.05}>
            <Card className="p-6">
              <div className="mb-4 flex items-center gap-2">
                <Award size={18} className="text-accent" />
                <h2 className="text-base font-semibold text-font-primary">
                  Mis reconocimientos
                </h2>
              </div>
              {awards.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-8 text-center">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-light">
                    <Trophy size={22} className="text-accent" />
                  </span>
                  <p className="text-sm text-font-secondary">
                    Aún no tienes premios registrados. ¡Sigue así!
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {awards.map((a) => (
                    <AwardCard key={a.id} record={a} />
                  ))}
                </div>
              )}
            </Card>
          </FadeIn>

          {/* Llamados de atención (si hay) */}
          {warnings.length > 0 && (
            <FadeIn delay={0.08}>
              <Card className="p-6">
                <div className="mb-4 flex items-center gap-2">
                  <AlertTriangle size={18} className="text-warning" />
                  <h2 className="text-base font-semibold text-font-primary">
                    Llamados de atención
                  </h2>
                </div>
                <div className="flex flex-col gap-2">
                  {warnings.map((w) => (
                    <WarningRow key={w.id} record={w} />
                  ))}
                </div>
              </Card>
            </FadeIn>
          )}

          {/* Anuncios estilo Facebook */}
          <FadeIn delay={0.1}>
            <div className="flex items-center gap-2">
              <Megaphone size={18} className="text-primary" />
              <h2 className="text-base font-semibold text-font-primary">Anuncios</h2>
            </div>
          </FadeIn>
          {posts.length === 0 ? (
            <Card className="p-8 text-center text-sm text-font-tertiary">
              No hay anuncios por el momento.
            </Card>
          ) : (
            <div className="flex flex-col gap-4">
              {posts.map((p, i) => (
                <FadeIn key={p.id} delay={0.12 + i * 0.04}>
                  <AnnouncementPost post={p} />
                </FadeIn>
              ))}
            </div>
          )}
        </div>

        {/* Columna lateral: Vacaciones + feriados */}
        <div className="flex flex-col gap-6">
          <FadeIn delay={0.05}>
            <Card className="p-6">
              <div className="mb-4 flex items-center gap-2">
                <CalendarHeart size={18} className="text-primary" />
                <h2 className="text-base font-semibold text-font-primary">
                  Mis vacaciones
                </h2>
              </div>

              {daysAvailable != null && (
                <div className="mb-4 rounded-xl bg-primary-subtle p-4 text-center">
                  <p className="text-3xl font-bold text-primary">{daysAvailable}</p>
                  <p className="text-xs font-medium text-font-secondary">
                    días disponibles
                  </p>
                </div>
              )}

              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-font-tertiary">
                Próximas aprobadas
              </p>
              {upcomingLeaves.length === 0 ? (
                <p className="text-sm text-font-tertiary">
                  No tienes vacaciones aprobadas próximas.{' '}
                  <Link to="/permisos" className="font-semibold text-primary hover:underline">
                    Solicitar
                  </Link>
                </p>
              ) : (
                <div className="flex flex-col gap-2">
                  {upcomingLeaves.map((l: any) => (
                    <div
                      key={l.id}
                      className="flex items-center gap-3 rounded-lg border border-border p-2.5"
                    >
                      <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-accent-light">
                        <CalendarClock size={16} className="text-accent" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-font-primary">
                          {fmtShort(l.startDate)} – {fmtShort(l.endDate)}
                        </p>
                        <p className="text-xs text-font-tertiary">
                          {l.workingDays} día(s) · Aprobado
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </FadeIn>

          <FadeIn delay={0.08}>
            <Card className="p-6">
              <div className="mb-4 flex items-center gap-2">
                <PartyPopper size={18} className="text-info" />
                <h2 className="text-base font-semibold text-font-primary">
                  Próximos feriados {me?.employee?.country?.nameEs ? `· ${me.employee.country.nameEs}` : ''}
                </h2>
              </div>
              {upcomingHolidays.length === 0 ? (
                <p className="text-sm text-font-tertiary">Sin feriados próximos registrados.</p>
              ) : (
                <div className="flex flex-col gap-2">
                  {upcomingHolidays.map((h: any) => {
                    const dp = dateParts(h.date);
                    return (
                      <div key={h.id} className="flex items-center gap-3">
                        <span className="flex h-9 w-9 flex-shrink-0 flex-col items-center justify-center rounded-lg bg-blue-50 text-info">
                          <span className="text-xs font-bold leading-none">{dp.day}</span>
                          <span className="text-[8px] uppercase leading-none">{dp.month}</span>
                        </span>
                        <p className="text-sm text-font-primary">{h.name}</p>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          </FadeIn>
        </div>
      </div>
    </PageTransition>
  );
}

function AwardCard({ record }: { record: EmployeeRecord }) {
  return (
    <div className="lift rounded-xl border border-accent/30 bg-accent-light/40 p-4">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-white text-accent shadow-sm">
          <Trophy size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-font-primary">{record.title}</p>
          {record.description && (
            <p className="mt-0.5 line-clamp-2 text-xs text-font-secondary">
              {record.description}
            </p>
          )}
          <div className="mt-1.5 flex items-center gap-2">
            <span className="text-[11px] text-font-tertiary">{fmtDate(record.occurredOn)}</span>
            {record.points ? (
              <Badge variant="success">
                <Star size={10} /> {record.points} pts
              </Badge>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

const SEVERITY: Record<string, { label: string; variant: 'warning' | 'error' | 'neutral' }> = {
  MINOR: { label: 'Leve', variant: 'neutral' },
  MODERATE: { label: 'Moderada', variant: 'warning' },
  SEVERE: { label: 'Grave', variant: 'error' },
};

function WarningRow({ record }: { record: EmployeeRecord }) {
  const sev = record.severity ? SEVERITY[record.severity] : null;
  return (
    <div className="flex items-start gap-3 rounded-lg border border-warning/30 bg-yellow-50 p-3">
      <AlertTriangle size={16} className="mt-0.5 flex-shrink-0 text-warning" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold text-font-primary">{record.title}</p>
          {sev && <Badge variant={sev.variant}>{sev.label}</Badge>}
        </div>
        {record.description && (
          <p className="mt-0.5 text-xs text-font-secondary">{record.description}</p>
        )}
        <p className="mt-1 text-[11px] text-font-tertiary">{fmtDate(record.occurredOn)}</p>
      </div>
    </div>
  );
}

/** Anuncio renderizado como post de red social. */
function AnnouncementPost({ post }: { post: AnnouncementListItem }) {
  const authorName = post.createdBy?.employee
    ? `${post.createdBy.employee.firstName} ${post.createdBy.employee.lastName}`
    : post.createdBy?.email ?? 'ISOFT';
  const authorPhoto = post.createdBy?.employee?.photoUrl;
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center gap-3 p-4 pb-3">
        {authorPhoto ? (
          <img src={authorPhoto} alt={authorName} className="h-10 w-10 rounded-full object-cover" />
        ) : (
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary-light text-sm font-semibold text-white">
            {initials(authorName)}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-font-primary">{authorName}</p>
          <p className="text-xs text-font-tertiary">
            {fmtDate(post.publishedAt ?? post.createdAt)}
          </p>
        </div>
      </div>

      <div className="px-4">
        <h3 className="text-base font-bold text-font-primary">{post.title}</h3>
        <p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-font-primary">
          {post.body}
        </p>
      </div>

      <div className="px-4 pb-4">
        <AnnouncementMedia attachments={post.attachments} />
      </div>

      <Link
        to={`/anuncios/${post.id}`}
        className="block border-t border-border px-4 py-2.5 text-center text-xs font-semibold text-primary transition hover:bg-primary-subtle"
      >
        Ver anuncio completo
      </Link>
    </Card>
  );
}
